import { createStyles } from 'antd-style';
import {
  type CSSProperties,
  type RefObject,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

const useStyles = createStyles(({ css }) => ({
  // 让带 scroll.y 的 Table body 至少占满计算出的填充高度:行少时也撑满、不塌;
  // 行多时 scroll.y 的 max-height 接管滚动。展开行内的嵌套表不受影响。
  wrap: css`
    & .ant-table-body {
      min-height: var(--qm-fill-y, 0px);
    }
    & .ant-table-expanded-row .ant-table-body,
    & .ant-table-expanded-row-fixed .ant-table-body {
      min-height: 0;
    }
  `,
}));

function getScrollParent(el: HTMLElement | null): HTMLElement | null {
  let p = el?.parentElement ?? null;
  while (p) {
    const oy = getComputedStyle(p).overflowY;
    if (oy === 'auto' || oy === 'scroll') return p;
    p = p.parentElement;
  }
  return null;
}

interface FillTableResult {
  /** 直接传给 <Table scroll={{ y: scrollY }} /> */
  scrollY: number;
  /** 展开后铺到包住 <Table> 的 <div> 上 */
  wrapperProps: {
    ref: RefObject<HTMLDivElement | null>;
    className: string;
    style: CSSProperties;
  };
}

/**
 * 让分页表格固定高度填满所在滚动容器:行少不塌、行多内部滚动、分页钉底。
 * dep(行数等)变化时重算。仅用于页面级列表表格,Modal / 嵌套表不要用。
 */
export function useFillTable(dep?: unknown): FillTableResult {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [y, setY] = useState(400);
  const { styles } = useStyles();

  useLayoutEffect(() => {
    const recompute = () => {
      const wrap = wrapRef.current;
      const body = wrap?.querySelector('.ant-table-body') as HTMLElement | null;
      const anchor = body ?? wrap;
      if (!wrap || !anchor) return;
      const scroller = getScrollParent(wrap) ?? document.documentElement;
      const avail =
        scroller.getBoundingClientRect().bottom -
        anchor.getBoundingClientRect().top -
        // 给分页条 + 卡片内边距留白
        72;
      setY(Math.max(220, Math.floor(avail)));
    };
    recompute();
    const ro = new ResizeObserver(recompute);
    if (wrapRef.current) ro.observe(wrapRef.current);
    const scroller = getScrollParent(wrapRef.current);
    if (scroller) ro.observe(scroller);
    window.addEventListener('resize', recompute);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', recompute);
    };
  }, [dep]);

  return {
    scrollY: y,
    wrapperProps: {
      ref: wrapRef,
      className: styles.wrap,
      style: { '--qm-fill-y': `${y}px` } as CSSProperties,
    },
  };
}
